import { useState, type ReactElement } from 'react';
import { Checkbox, Group, Loader, ScrollArea, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { Plus, Search } from 'lucide-react';

const MAX_FREE_VALUE_LENGTH = 120;

export interface FilterChecklistOption {
    value: string;
    label: string;
    icon?: ReactElement | null;
}

interface FilterValueChecklistProps {
    options: FilterChecklistOption[];
    selected: string[];
    onToggle: (value: string, checked: boolean) => void;
    loading?: boolean;
    /** Filter the option list client-side as the user types. */
    searchable?: boolean;
    /** Async mode: the caller reloads `options` per query; the search box is always shown. */
    onSearch?: (query: string) => void;
    /** Offer a "Filter by …" row that applies search text matching no option verbatim. */
    allowFreeValue?: boolean;
    searchPlaceholder?: string;
    emptyMessage?: string;
    /** Label for a selected value that is missing from `options` (defaults to the raw value). */
    labelFor?: (value: string) => string;
}

/**
 * A searchable checkbox list over filter values. Toggling applies immediately —
 * there is no Apply/Cancel step — so the same component serves both the
 * Add-filter accordion sections and the active-filters editor.
 */
export default function FilterValueChecklist({
    options,
    selected,
    onToggle,
    loading = false,
    searchable = false,
    onSearch,
    allowFreeValue = false,
    searchPlaceholder = 'Search…',
    emptyMessage = 'No values',
    labelFor,
}: FilterValueChecklistProps): ReactElement {
    const [query, setQuery] = useState('');
    const trimmed = query.trim();
    const showSearch = searchable || onSearch !== undefined;

    const changeQuery = (next: string): void => {
        setQuery(next);
        onSearch?.(next);
    };

    const matches = (option: FilterChecklistOption): boolean =>
        trimmed.length === 0 || option.label.toLowerCase().includes(trimmed.toLowerCase()) || option.value.toLowerCase().includes(trimmed.toLowerCase());
    const visible = onSearch !== undefined ? options : options.filter(matches);

    const pinnedSelectionsMissingFromOptions = selected.filter((value) => !options.some((option) => option.value === value));

    const offerFreeValue =
        allowFreeValue &&
        trimmed.length > 0 &&
        trimmed.length <= MAX_FREE_VALUE_LENGTH &&
        !selected.includes(trimmed) &&
        !options.some((option) => option.value.toLowerCase() === trimmed.toLowerCase());

    const row = (value: string, label: string, icon?: ReactElement | null): ReactElement => (
        <Checkbox
            key={value}
            size="sm"
            checked={selected.includes(value)}
            onChange={(event) => onToggle(value, event.currentTarget.checked)}
            label={
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                    {icon}
                    <Text size="sm" truncate>
                        {label}
                    </Text>
                </Group>
            }
            styles={{ body: { alignItems: 'center' }, labelWrapper: { minWidth: 0, flex: 1 } }}
        />
    );

    const empty = !loading && visible.length === 0 && pinnedSelectionsMissingFromOptions.length === 0 && !offerFreeValue;

    return (
        <Stack gap="xs">
            {showSearch && (
                <TextInput
                    aria-label="Search filter values"
                    size="xs"
                    value={query}
                    onChange={(event) => changeQuery(event.currentTarget.value)}
                    placeholder={searchPlaceholder}
                    leftSection={<Search size={13} />}
                    rightSection={loading ? <Loader size={13} /> : undefined}
                />
            )}
            {loading && !showSearch && (
                <Group justify="center" py="xs">
                    <Loader size={16} />
                </Group>
            )}
            {empty ? (
                <Text size="xs" c="dimmed" py={4}>
                    {emptyMessage}
                </Text>
            ) : (
                <ScrollArea.Autosize mah={240} type="auto">
                    <Stack gap={8} py={2}>
                        {pinnedSelectionsMissingFromOptions.map((value) => row(value, labelFor?.(value) ?? value))}
                        {visible.map((option) => row(option.value, option.label, option.icon))}
                        {offerFreeValue && (
                            <UnstyledButton
                                onClick={() => {
                                    onToggle(trimmed, true);
                                    changeQuery('');
                                }}
                            >
                                <Group gap={8} wrap="nowrap">
                                    <Plus size={13} style={{ color: 'var(--mantine-primary-color-filled)', flexShrink: 0 }} />
                                    <Text size="sm" c="var(--mantine-primary-color-filled)" truncate>
                                        Filter by “{trimmed}”
                                    </Text>
                                </Group>
                            </UnstyledButton>
                        )}
                    </Stack>
                </ScrollArea.Autosize>
            )}
        </Stack>
    );
}
